/**
* Shopping Cart Module
* Handles cart functionality, checkout, and PayPal integration
*/

// Cart state
let cart = [];
let cartTotal = 0;

// PayPal preloading state
let paypalPreloadAttempted = false;
let paypalPreloadPromise = null;

/**
* Check if functional cookies are allowed
*/
function isFunctionalCookiesAllowed()
{
    if (window.CookieConsentManager)
    {
        return window.CookieConsentManager.isCategoryAllowed('functional');
    }

    // If no consent manager, check localStorage for consent
    const consent = localStorage.getItem('cookie_consent');
    const preferences = localStorage.getItem('cookie_preferences');

    if (consent === 'true' && preferences)
    {
        try
        {
            const prefs = JSON.parse(preferences);
            return prefs.functional === true;
        }
        catch (e)
        {
            console.warn('Failed to parse cookie preferences:', e);
        }
    }

    // Default to true for necessary functionality
    return true;
}

/**
* Initialize shopping cart
*/
function initializeShoppingCart()
{
    loadCartFromStorage();
    updateCartDisplay();

    // Add event delegation for better performance with dynamic elements
    document.addEventListener('click', (event) =>
    {
        const target = event.target;

        // Handle cart item removal with event delegation
        if (target.classList.contains('remove-from-cart'))
        {
            const itemName = target.dataset.itemName;
            removeFromCart(itemName);
        }

        // Handle quantity updates with event delegation
        if (target.classList.contains('update-quantity'))
        {
            const action = target.dataset.action;
            const itemName = target.closest('.cart-item').querySelector('.item-name').textContent;

            if (action === 'increase')
            {
                updateCartItemQuantity(itemName, 1);
            }
            else if (action === 'decrease')
            {
                updateCartItemQuantity(itemName, -1);
            }
        }
    });

    // Save cart before page unload as a safety measure
    window.addEventListener('beforeunload', () =>
    {
        saveCartToStorage();
    });

    // Also save cart when page visibility changes (e.g., tab switch)
    document.addEventListener('visibilitychange', () =>
    {
        if (document.hidden)
        {
            saveCartToStorage();
        }
    });
}

/**
* Load cart from localStorage
*/
function loadCartFromStorage()
{
    try
    {
        // Only load from storage if functional cookies are allowed
        if (isFunctionalCookiesAllowed())
        {
            const storedCart = localStorage.getItem('cart');
            if (storedCart)
            {
                const parsedCart = JSON.parse(storedCart);
                // Validate that parsed cart is an array
                if (Array.isArray(parsedCart))
                {
                    cart = parsedCart;
                }
                else
                {
                    console.warn('Cart data is not an array, resetting cart');
                    cart = [];
                    clearCartStorage();
                }
            }
            else
            {
                // No stored cart, start with empty cart
                cart = [];
            }
        }
        else
        {
            // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('Cart: Functional cookies not allowed, using empty cart');
            cart = [];
        }
    }
    catch (error)
    {
        console.error('Error loading cart from storage:', error);
        cart = [];
        // Clear corrupted cart data
        try
        {
            clearCartStorage();
        } catch (clearError)
        {
            console.error('Error clearing corrupted cart storage:', clearError);
        }
    }

    // Always recalculate cart total and update display after loading
    updateCartDisplay();
}

/**
* Save cart to localStorage
*/
function saveCartToStorage()
{
    try
    {
        // Only save to storage if functional cookies are allowed
        if (isFunctionalCookiesAllowed())
        {
            // Ensure cart is an array before saving
            if (Array.isArray(cart))
            {
                localStorage.setItem('cart', JSON.stringify(cart));
            }
            else
            {
                console.warn('Cart is not an array, cannot save to storage');
                cart = [];
            }
        }
        else
        {
            // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('Cart: Functional cookies not allowed, not saving cart to storage');
        }
    }
    catch (error)
    {
        console.error('Error saving cart to storage:', error);
        // Try to handle quota exceeded error
        if (error.name === 'QuotaExceededError' || error.code === 22)
        {
            console.warn('localStorage quota exceeded, clearing old cart data');
            try
            {
                clearCartStorage();
            } catch (clearError)
            {
                console.error('Error clearing cart storage:', clearError);
            }
        }
    }
}

/**
* Clear cart from localStorage
*/
function clearCartStorage()
{
    localStorage.removeItem('cart');
}

/**
* Add item to shopping cart with quantity management
* 
* This function adds an item to the shopping cart, handling both new items
* and existing items by incrementing quantity. It checks for existing items
* based on name and options to prevent duplicates and instead increases
* the quantity of matching items.
* 
* Features:
* - Restricts cart to only one main item (and its options) at a time
* - Prevents adding different main items when one already exists
* - Allows adding same main item multiple times (increments quantity)
* - Allows adding options to existing main item
* - Duplicate item detection based on name and options
* - Quantity increment for existing items
* - New item addition for unique items
* - Cart display update
* - Persistent storage to localStorage
* - Optional user feedback through notifications
* 
* @param {Object} item - Item object containing name, price, description, and options
* @param {boolean} showNotification - Whether to show individual notification (default: true)
* @returns {boolean} True if item was added successfully, false otherwise
*/
function addToCart(item, showNotification = true)
{
    // Validate that options can only be added with a main product
    if (item.isMainItem === false && item.parentItemId)
    {
        // Check if the parent main item exists in the cart
        const parentExists = cart.some(cartItem =>
            cartItem.itemId === item.parentItemId && cartItem.isMainItem === true
        );

        if (!parentExists)
        {
            console.error('Cannot add option without main product:', item.name);
            if (showNotification && window.UIUtils)
            {
                window.UIUtils.showNotification('Optionen können nur zusammen mit der Hauptleistung bestellt werden.', 'error');
            }
            return false;
        }
    }

    // NEW RESTRICTION: Only allow one main item in the cart at a time
    if (item.isMainItem === true)
    {
        // Find if there's already a main item in the cart
        const existingMainItem = cart.find(cartItem => cartItem.isMainItem === true);

        if (existingMainItem)
        {
            // Check if the item being added is different from the existing main item
            const isSameItem = existingMainItem.name === item.name &&
                JSON.stringify(existingMainItem.options) === JSON.stringify(item.options);

            if (!isSameItem)
            {
                // Different main item - prevent adding
                console.warn('Cannot add multiple different main items to cart:', item.name);
                // Always show this important warning, regardless of showNotification setting
                if (window.UIUtils)
                {
                    window.UIUtils.showNotification(
                        `Sie können nur eine Hauptleistung gleichzeitig im Warenkorb haben. Entfernen Sie zuerst "${existingMainItem.name}" um "${item.name}" hinzuzufügen.`,
                        'warning'
                    );
                }
                return false;
            }
            // If it's the same item, allow it to proceed (quantity will be incremented below)
        }
    }

    // Check if item already exists in cart (same name and options)
    const existingItemIndex = cart.findIndex(cartItem =>
        cartItem.name === item.name &&
        JSON.stringify(cartItem.options) === JSON.stringify(item.options)
    );

    if (existingItemIndex !== -1)
    {
        // Item already exists - prevent adding (max quantity = 1)
        console.warn('Item already exists in cart, maximum quantity is 1:', item.name);
        if (showNotification && window.UIUtils)
        {
            window.UIUtils.showNotification(
                `Dieser Artikel ist bereits im Warenkorb. Maximale Anzahl: 1`,
                'warning'
            );
        }
        return false;
    }
    else
    {
        // Add new item to cart with quantity of 1
        cart.push(
            {
                ...item,
                quantity: 1
            });
    }

    // Update cart display and save to storage
    updateCartDisplay();
    saveCartToStorage();

    // Show success notification to user (only if requested)
    if (showNotification && window.UIUtils)
    {
        window.UIUtils.showNotification(`${item.name} wurde zum Warenkorb hinzugefügt`, 'success');
    }

    return true;
}

/**
* Update cart display
*/
function updateCartDisplay()
{
    // Clean up any orphaned options first
    cleanupOrphanedOptions();

    // Calculate total (ensure it's always a number)
    cartTotal = cart.reduce((total, item) =>
    {
        const itemPrice = (item.price !== undefined && item.price !== null) ? Number(item.price) : 0;
        const itemQuantity = (item.quantity !== undefined && item.quantity !== null) ? Number(item.quantity) : 1;
        return total + (itemPrice * itemQuantity);
    }, 0);

    const cartCountElement = document.getElementById('cartCount');
    if (cartCountElement)
    {
        const itemCount = cart.reduce((total, item) =>
        {
            const itemQuantity = (item.quantity !== undefined && item.quantity !== null) ? Number(item.quantity) : 1;
            return total + itemQuantity;
        }, 0);
        cartCountElement.textContent = itemCount;
        cartCountElement.style.display = itemCount > 0 ? 'flex' : 'none';
    }

    // Also update global cartTotal for other functions
    window.cartTotal = cartTotal;
}

/**
* Remove item from cart
* @param {number} index - Item index
*/
function removeFromCart(index)
{
    const itemToRemove = cart[index];
    console.log('Removing item:', itemToRemove);

    // If removing a main tuning item, clear the entire cart
    if (itemToRemove && itemToRemove.isMainItem && itemToRemove.name && itemToRemove.name.includes('Tuning'))
    {
        // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('Clearing entire cart for main tuning item');
        cart = []; // Clear entire cart
    }
    // If removing any other main item, also remove all related options
    else if (itemToRemove && itemToRemove.isMainItem && itemToRemove.itemId)
    {
        console.log('Removing main item and related options:', itemToRemove.itemId);

        // Count related options before removal
        const relatedOptions = cart.filter(cartItem => cartItem.parentItemId === itemToRemove.itemId);
        console.log('Found related options:', relatedOptions.length);

        // Find and remove all options that belong to this main item
        cart = cart.filter(cartItem =>
            !(cartItem.parentItemId === itemToRemove.itemId)
        );

        // Find the new index of the main item after filtering
        const newIndex = cart.findIndex(cartItem => cartItem.itemId === itemToRemove.itemId);
        if (newIndex !== -1)
        {
            cart.splice(newIndex, 1);
            console.log('Removed main item at new index:', newIndex);
        }

        console.log('Cart after removal:', cart.length, 'items');
    }
    else
    {
        // Remove the item normally if it's not a main item
        console.log('Removing regular item at index:', index);
        cart.splice(index, 1);
    }

    updateCartDisplay();
    saveCartToStorage();

    // Check if cart is empty after removal
    if (cart.length === 0)
    {
        // Close the cart modal if it's open and restore scrolling
        const cartModal = document.getElementById('cartModal');
        if (cartModal)
        {
            cartModal.remove();
            // Update global close button
            if (window.UIUtils && window.UIUtils.updateGlobalCloseButton)
            {
                window.UIUtils.updateGlobalCloseButton();
            }
            // Restore scrolling on main page
            if (window.UIUtils && window.UIUtils.unlockPageScroll)
            {
                window.UIUtils.unlockPageScroll();
            }
            // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('Cart is empty, closing cart modal and restoring scroll');
        }
    }
    else
    {
        // Refresh cart modal content if it's open (without closing/reopening)
        refreshCartModalContent();
    }
}

/**
* Update cart item quantity
* @param {number} index - Item index
* @param {number} quantity - New quantity
*/
function updateCartItemQuantity(index, quantity)
{
    const itemToUpdate = cart[index];

    // Enforce maximum quantity of 1
    if (quantity > 1)
    {
        if (window.UIUtils)
        {
            window.UIUtils.showNotification(
                'Maximale Anzahl pro Artikel: 1',
                'warning'
            );
        }
        return; // Don't update the quantity
    }

    // If this is an option item, validate that quantity doesn't exceed main item quantity
    if (itemToUpdate && itemToUpdate.isMainItem === false && itemToUpdate.parentItemId)
    {
        // Find the parent main item
        const parentItem = cart.find(cartItem =>
            cartItem.itemId === itemToUpdate.parentItemId && cartItem.isMainItem === true
        );

        if (parentItem && quantity > parentItem.quantity)
        {
            // Show notification that quantity cannot exceed main item quantity
            if (window.UIUtils)
            {
                window.UIUtils.showNotification(
                    `Die Anzahl der Optionen kann nicht höher sein als die Anzahl der Hauptleistung (${parentItem.quantity})`,
                    'warning'
                );
            }
            return; // Don't update the quantity
        }
    }

    if (quantity <= 0)
    {
        // If quantity becomes 0 or less, remove the item
        if (itemToUpdate && itemToUpdate.isMainItem && itemToUpdate.name && itemToUpdate.name.includes('Tuning'))
        {
            // If it's the main tuning product, clear entire cart
            // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('Main tuning product quantity reached 0, clearing entire cart');
            cart = [];
        }
        else if (itemToUpdate && itemToUpdate.isMainItem && itemToUpdate.itemId)
        {
            // If it's any other main item, remove it and all related options
            console.log('Main item quantity reached 0, removing item and related options:', itemToUpdate.itemId);

            // Remove all options that belong to this main item
            cart = cart.filter(cartItem =>
                !(cartItem.parentItemId === itemToUpdate.itemId)
            );

            // Find and remove the main item
            const newIndex = cart.findIndex(cartItem => cartItem.itemId === itemToUpdate.itemId);
            if (newIndex !== -1)
            {
                cart.splice(newIndex, 1);
            }
        }
        else
        {
            // Remove the item normally if it's not a main item
            cart.splice(index, 1);
        }
    }
    else
    {
        cart[index].quantity = quantity;

        // If this is a main item and its quantity was reduced, adjust related options
        if (itemToUpdate && itemToUpdate.isMainItem === true && itemToUpdate.itemId)
        {
            // Find all options that belong to this main item
            const relatedOptions = cart.filter(cartItem =>
                cartItem.parentItemId === itemToUpdate.itemId && cartItem.isMainItem === false
            );

            // If main item quantity was reduced, adjust option quantities
            relatedOptions.forEach(option =>
            {
                if (option.quantity > quantity)
                {
                    option.quantity = quantity;
                    // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log(`Adjusted option ${option.name} quantity to ${quantity} to match main item`);
                }
            });
        }
    }

    updateCartDisplay();
    saveCartToStorage();

    // Check if cart is empty after quantity update
    if (cart.length === 0)
    {
        // Close the cart modal if it's open and restore scrolling
        const cartModal = document.getElementById('cartModal');
        if (cartModal)
        {
            cartModal.remove();
            // Update global close button
            if (window.UIUtils && window.UIUtils.updateGlobalCloseButton)
            {
                window.UIUtils.updateGlobalCloseButton();
            }
            // Restore scrolling on main page
            if (window.UIUtils && window.UIUtils.unlockPageScroll)
            {
                window.UIUtils.unlockPageScroll();
            }
            // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('Cart is empty after quantity update, closing cart modal and restoring scroll');
        }
    }
    else
    {
        // Refresh cart modal content if it's open (without closing/reopening)
        refreshCartModalContent();
    }
}

/**
* Clear entire cart
*/
function clearCart()
{
    cart = [];
    updateCartDisplay();
    saveCartToStorage();

    // Refresh cart modal content if it's open (without closing/reopening)
    refreshCartModalContent();
}

/**
* Refresh cart modal content without closing/reopening the modal
* This prevents screen flashing when updating cart items
*/
function refreshCartModalContent()
{
    const cartModal = document.getElementById('cartModal');
    if (!cartModal)
    {
        return; // Modal is not open
    }

    // Find the cartItems container within the modal
    const cartItemsContainer = cartModal.querySelector('#cartItems');
    if (!cartItemsContainer)
    {
        return; // Container not found
    }

    // Generate new cart content
    let cartHTML = '';

    // Handle empty cart state
    if (cart.length === 0)
    {
        cartHTML = `
        <div style="text-align: center; padding: 2rem;">
        <p style="color: #666; font-size: 1.1rem;">Ihr Warenkorb ist leer</p>
        <p style="color: #999; font-size: 0.9rem; margin-top: 0.5rem;">Fügen Sie Artikel hinzu, um zu beginnen</p>
        </div>
        `;
    }
    else
    {
        // Display cart items with management controls
        cart.forEach((item, index) =>
        {
            // Debug: Log item name to ensure consistency with order cards
            // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('🔍 [CART] Item name:', item.name);

            // Safely handle price - use 0 if undefined or null
            const itemPrice = (item.price !== undefined && item.price !== null) ? Number(item.price) : 0;

            cartHTML += `
            <div style="padding: 1rem; border: 1px solid #eee; border-radius: 8px; margin-bottom: 1rem;">
            <div style="margin-bottom: 1rem;">
            <h4 style="margin: 0 0 0.5rem 0; color: #333;">${item.name || 'Unbenannter Artikel'}</h4>
            <p style="margin: 0; color: #666; font-size: 0.9rem;">${item.description || ''}</p>
            </div>
            <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 0.5rem;">
            <p style="margin: 0; font-weight: 600; color: #667eea;">€${itemPrice.toFixed(2)}</p>
            <div style="display: flex; align-items: center; gap: 0.2rem;">
            <button onclick="removeFromCart(${index})" style="background: #dc3545; color: white; border: none; padding: 5px 10px; border-radius: 4px; cursor: pointer; font-size: 14px;" title="Artikel entfernen">✕</button>
            </div>
            </div>
            </div>
            `;
        });
    }

    // Update the cart items container
    cartItemsContainer.innerHTML = cartHTML;

    // Update total price section if it exists
    const totalSection = cartModal.querySelector('div[style*="border-top: 2px solid #667eea"]');
    if (totalSection)
    {
        const totalPriceElement = totalSection.querySelector('span[style*="color: #667eea"]');
        if (totalPriceElement)
        {
            totalPriceElement.textContent = `€${cartTotal.toFixed(2)}`;
            console.log('Total price updated in modal:', cartTotal);
        }
        else
        {
            // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('Total price element not found in modal');
        }
    }
    else
    {
        // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('Total section not found in modal');
    }
}

/**
* Toggle cart modal visibility with comprehensive cart display
* 
* This function toggles the visibility of the shopping cart modal, showing
* all cart items with their details, quantities, and total price. It handles
* both empty cart state and populated cart state with different UI layouts.
* The modal includes item management controls and checkout functionality.
* 
* Features:
* - Modal toggle functionality (show/hide)
* - Empty cart state handling
* - Item display with quantity controls
* - Total price calculation and display
* - Item removal and quantity adjustment buttons
* - Checkout and clear cart functionality
* - Responsive modal design
* - Click-outside-to-close functionality
* 
* @returns {void} No return value
*/
function toggleCart()
{
    const existingModal = document.getElementById('cartModal');
    if (existingModal)
    {
        // Close cart modal and unlock scroll
        existingModal.remove();
        // Update global close button
        if (window.UIUtils && window.UIUtils.updateGlobalCloseButton)
        {
            window.UIUtils.updateGlobalCloseButton();
        }
        if (window.UIUtils && window.UIUtils.unlockPageScroll)
        {
            window.UIUtils.unlockPageScroll();
        }
        return;
    }

    const modal = document.createElement('div');
    modal.id = 'cartModal';
    // Check if mobile device
    const isMobile = window.innerWidth <= 768;

    modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${isMobile ? '#000000' : 'rgba(0,0,0,0.8)'}; /* Fully opaque on mobile to hide content below */
    z-index: ${isMobile ? '10001' : '11000'} !important; /* Above header (10000) on desktop */
    display: flex;
    align-items: center; /* Centered on mobile */
    justify-content: center;
    padding: ${isMobile ? '1rem' : '2rem'};
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    `;

    // Global close button will be managed by UIUtils.updateGlobalCloseButton()

    const modalContent = document.createElement('div');
    modalContent.style.cssText = `
    background: white;
    border-radius: 15px;
    padding: ${isMobile ? '1rem' : '2rem'};
    max-width: ${isMobile ? 'calc(100vw - 2rem)' : '600px'};
    width: 95%;
    max-height: ${isMobile ? '90vh' : '90vh'}; /* Use full viewport height on mobile */
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    box-shadow: 0 20px 40px rgba(0,0,0,0.3);
    position: relative;
    margin-top: 0;
    `;

    let cartHTML = `
    <h2 style="color: #667eea; margin-bottom: 2rem;">🛒 Warenkorb</h2>
    <div id="cartItems">
    `;

    // Handle empty cart state
    if (cart.length === 0)
    {
        cartHTML += `
        <div style="text-align: center; padding: 2rem;">
        <p style="color: #666; font-size: 1.1rem;">Ihr Warenkorb ist leer</p>
        <p style="color: #999; font-size: 0.9rem; margin-top: 0.5rem;">Fügen Sie Artikel hinzu, um zu beginnen</p>
        </div>
        `;
    }
    else
    {
        // Display cart items with management controls
        cart.forEach((item, index) =>
        {
            // Debug: Log item name to ensure consistency with order cards
            // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('🔍 [CART MODAL] Item name:', item.name);

            // Safely handle price - use 0 if undefined or null
            const itemPrice = (item.price !== undefined && item.price !== null) ? Number(item.price) : 0;

            cartHTML += `
            <div style="padding: 1rem; border: 1px solid #eee; border-radius: 8px; margin-bottom: 1rem;">
            <div style="margin-bottom: 1rem;">
            <h4 style="margin: 0 0 0.5rem 0; color: #333;">${item.name || 'Unbenannter Artikel'}</h4>
            <p style="margin: 0; color: #666; font-size: 0.9rem;">${item.description || ''}</p>
            </div>
            <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 0.5rem;">
            <p style="margin: 0; font-weight: 600; color: #667eea;">€${itemPrice.toFixed(2)}</p>
            <div style="display: flex; align-items: center; gap: 0.2rem;">
            <button onclick="removeFromCart(${index})" style="background: #dc3545; color: white; border: none; padding: 5px 10px; border-radius: 4px; cursor: pointer; font-size: 14px;" title="Artikel entfernen">✕</button>
            </div>
            </div>
            </div>
            `;
        });

        cartHTML += '</div>';

        // Add total price and action buttons
        cartHTML += `
        <div style="border-top: 2px solid #667eea; padding-top: 1rem; margin-bottom: 1.5rem;">
        <div style="display: flex; justify-content: space-between; align-items: center; font-size: 1.2rem; font-weight: 600; color: #333;">
        <span>Gesamt:</span>
        <span style="color: #667eea;">€${cartTotal.toFixed(2)}</span>
        </div>
        </div>

        <div style="display: flex; gap: 1rem; flex-wrap: wrap;">
        <button onclick="proceedToCheckout()" style="flex: 1; background: #28a745; color: white; border: none; padding: 12px 24px; border-radius: 8px; cursor: pointer; font-weight: 600;">🛒 Zur Kasse</button>
        <button onclick="clearCart(); toggleCart();" style="background: #dc3545; color: white; border: none; padding: 12px 24px; border-radius: 8px; cursor: pointer; font-size: 14px;">✕ Leeren</button>
        </div>
        `;
    }

    modalContent.innerHTML = cartHTML;
    modal.appendChild(modalContent);
    document.body.appendChild(modal);

    // Lock page scroll when cart modal is opened
    if (window.UIUtils && window.UIUtils.lockPageScroll)
    {
        window.UIUtils.lockPageScroll();
    }

    // Update global close button - use requestAnimationFrame to ensure modal is fully rendered
    if (window.UIUtils && window.UIUtils.updateGlobalCloseButton)
    {
        requestAnimationFrame(() =>
        {
            window.UIUtils.updateGlobalCloseButton();
        });
    }

    // Preload PayPal SDK and integration module when cart is opened for the first time
    // This ensures PayPal is ready when user proceeds to checkout
    if (!paypalPreloadAttempted && cart.length > 0)
    {
        paypalPreloadAttempted = true;
        // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('Preloading PayPal SDK when cart opened for the first time');

        // Preload in background - don't block UI
        paypalPreloadPromise = loadPayPalIntegrationModule()
            .then(() =>
            {
                // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('PayPal integration module preloaded successfully');
                return loadPayPalSDK();
            })
            .then(() =>
            {
                // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('PayPal SDK preloaded successfully - ready for checkout');
            })
            .catch((error) =>
            {
                console.warn('PayPal preload failed (will retry at checkout):', error.message || error);
                console.warn('PayPal preload error details:', {
                    message: error.message,
                    name: error.name,
                    stack: error.stack
                });
                // Reset flag so we can retry at checkout
                paypalPreloadAttempted = false;
            });
    }

    // Remove click-outside-to-close functionality - users must use the X button
}

/**
* Clean up orphaned options (options without their main product)
*/
function cleanupOrphanedOptions()
{
    const mainItemIds = cart
        .filter(item => item.isMainItem === true)
        .map(item => item.itemId);

    const originalLength = cart.length;
    cart = cart.filter(item =>
    {
        if (item.isMainItem === false && item.parentItemId)
        {
            return mainItemIds.includes(item.parentItemId);
        }
        return true;
    });

    if (cart.length !== originalLength)
    {
        // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log(`Cleaned up ${originalLength - cart.length} orphaned options`);
        updateCartDisplay();
        saveCartToStorage();
    }
}

/**
* Get cart total
*/
function getCartTotal()
{
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
}

/**
* Get cart data for PayPal
*/
function getCartData()
{
    return {
        items: cart,
        total: cartTotal,
        itemCount: cart.reduce((total, item) => total + item.quantity, 0)
    };
}

/**
* Update PayPal Pay Later banner amount dynamically
* @param {string} bannerId - The ID of the banner element
* @param {number} amount - The amount to display in the banner
*/
function updatePayPalPayLaterBanner(bannerId, amount)
{
    const banner = document.getElementById(bannerId);
    if (banner)
    {
        // Update the data-pp-amount attribute
        // The PayPal SDK will automatically detect the change and update the banner
        banner.setAttribute('data-pp-amount', amount.toFixed(2));

        // Note: PayPal SDK Messages component automatically watches for data-pp-amount changes
        // and updates the banner content accordingly
    }
}

/**
* Extract customer info from form or logged-in user
*/
function extractCustomerInfo()
{
    // Check if user is logged in
    if (window.AccountManagement && window.AccountManagement.getCurrentUser())
    {
        const user = window.AccountManagement.getCurrentUser();
        return {
            name: user.name,
            email: user.email,
            phone: user.phone || '',
            address: `${user.address || ''}, ${user.city || ''} ${user.postal_code || ''}`.trim()
        };
    }

    return null;
}

/**
* Get PayPal client ID from server
* @returns {Promise<string>} Promise that resolves with PayPal client ID
*/
async function getPayPalClientId()
{
    try
    {
        const response = await fetch('/api/config/paypal-client-id');

        if (!response.ok)
        {
            const errorText = await response.text();
            console.error('PayPal Client ID endpoint returned error:', response.status, errorText);
            throw new Error(`Failed to fetch PayPal Client ID: HTTP ${response.status}`);
        }

        const data = await response.json();

        if (data.clientId)
        {
            // Debug logging
            const isDebug = (typeof window !== 'undefined' && window.location &&
                (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ||
                    new URLSearchParams(window.location.search).get('debug') === 'true'));
            if (isDebug)
            {
                console.log('PayPal Client ID fetched successfully:', data.clientId ? 'Configured' : 'Not configured');
            }

            return data.clientId;
        }
        else
        {
            console.error('PayPal Client ID not available from server response:', data);
            throw new Error('PayPal Client ID not configured');
        }
    }
    catch (error)
    {
        console.error('Error fetching PayPal Client ID:', error);
        // Re-throw with more context
        throw new Error(`PayPal configuration error: ${error.message}`);
    }
}

/**
* Load PayPal SDK dynamically when needed
* @returns {Promise<void>} Promise that resolves when PayPal SDK is loaded
*/
async function loadPayPalSDK()
{
    return new Promise(async (resolve, reject) =>
    {
        // Check if PayPal SDK is already loaded
        if (typeof paypal !== 'undefined')
        {
            resolve();
            return;
        }

        // Check if script is already being loaded
        if (document.querySelector('script[src*="paypal.com/sdk"]'))
        {
            // Wait for existing script to load
            const checkInterval = setInterval(() =>
            {
                if (typeof paypal !== 'undefined')
                {
                    clearInterval(checkInterval);
                    resolve();
                }
            }, 100);

            // Use longer timeout on mobile devices
            const isMobile = window.innerWidth <= 768;
            setTimeout(() =>
            {
                clearInterval(checkInterval);
                reject(new Error('PayPal SDK loading timeout'));
            }, isMobile ? 15000 : 10000);
            return;
        }

        try
        {
            // Fetch PayPal client ID from server
            const paypalClientId = await getPayPalClientId();

            // Load PayPal SDK script dynamically
            const script = document.createElement('script');
            // Add mobile-specific parameters for better compatibility
            const isMobile = window.innerWidth <= 768;
            script.src = `https://www.paypal.com/sdk/js?client-id=${paypalClientId}&currency=EUR&components=buttons`;
            script.async = true;
            script.defer = false; // Don't defer on mobile to ensure it loads

            // Add crossorigin attribute for better mobile compatibility
            script.crossOrigin = 'anonymous';

            console.log('Loading PayPal SDK script:', {
                src: script.src,
                isMobile,
                async: script.async,
                crossOrigin: script.crossOrigin
            });

            script.onload = () =>
            {
                // Wait a bit for PayPal SDK to initialize
                // Use longer wait time on mobile devices
                const checkInterval = setInterval(() =>
                {
                    if (typeof paypal !== 'undefined')
                    {
                        clearInterval(checkInterval);
                        resolve();
                    }
                }, 100);

                // Timeout after longer period on mobile (8 seconds) vs desktop (5 seconds)
                setTimeout(() =>
                {
                    clearInterval(checkInterval);
                    if (typeof paypal !== 'undefined')
                    {
                        resolve();
                    }
                    else
                    {
                        reject(new Error('PayPal SDK failed to initialize'));
                    }
                }, isMobile ? 8000 : 5000);
            };

            script.onerror = (error) =>
            {
                console.error('PayPal SDK script loading error:', error);
                console.error('PayPal SDK script source:', script.src);
                reject(new Error(`Failed to load PayPal SDK script from ${script.src} - check network connection and CORS settings`));
            };

            // Append to head for better compatibility, especially on mobile devices
            document.head.appendChild(script);
        }
        catch (error)
        {
            console.error('Error loading PayPal SDK:', error);
            console.error('Error details:', {
                message: error.message,
                stack: error.stack,
                name: error.name
            });

            // Provide more helpful error message
            let errorMessage = 'Failed to load PayPal SDK';
            if (error.message.includes('PayPal Client ID'))
            {
                errorMessage = 'PayPal is not configured. Please check server configuration.';
            }
            else if (error.message.includes('network') || error.message.includes('fetch'))
            {
                errorMessage = 'Network error while loading PayPal. Please check your internet connection.';
            }
            else
            {
                errorMessage = `PayPal SDK error: ${error.message}`;
            }

            reject(new Error(errorMessage));
        }
    });
}

/**
* Load PayPal integration module if not already loaded
* @returns {Promise<void>} Promise that resolves when module is loaded
*/
function loadPayPalIntegrationModule()
{
    return new Promise((resolve, reject) =>
    {
        // Check if module is already loaded
        if (window.PayPalIntegration)
        {
            resolve();
            return;
        }

        // Check if script is already being loaded
        const existingScript = document.querySelector('script[src*="paypal-integration.js"]');
        if (existingScript)
        {
            // Wait for existing script to load
            const checkInterval = setInterval(() =>
            {
                if (window.PayPalIntegration)
                {
                    clearInterval(checkInterval);
                    resolve();
                }
            }, 100);

            // Use longer timeout on mobile devices
            const isMobile = window.innerWidth <= 768;
            setTimeout(() =>
            {
                clearInterval(checkInterval);
                if (window.PayPalIntegration)
                {
                    resolve();
                }
                else
                {
                    reject(new Error('PayPal integration module loading timeout'));
                }
            }, isMobile ? 8000 : 5000);
            return;
        }

        // Load PayPal integration module dynamically
        const script = document.createElement('script');
        script.src = 'js/paypal-integration.js?v=20251019';

        script.onload = () =>
        {
            // Wait a bit for module to initialize
            const checkInterval = setInterval(() =>
            {
                if (window.PayPalIntegration)
                {
                    clearInterval(checkInterval);
                    resolve();
                }
            }, 100);

            // Use longer timeout on mobile devices
            const isMobile = window.innerWidth <= 768;
            setTimeout(() =>
            {
                clearInterval(checkInterval);
                if (window.PayPalIntegration)
                {
                    resolve();
                }
                else
                {
                    reject(new Error('PayPal integration module failed to initialize'));
                }
            }, isMobile ? 5000 : 3000);
        };

        script.onerror = () =>
        {
            reject(new Error('Failed to load PayPal integration module'));
        };

        // Append to head for better compatibility, especially on mobile devices
        document.head.appendChild(script);
    });
}

/**
* Proceed to checkout (simplified version matching old index.html)
*/
function proceedToCheckout()
{
    if (cart.length === 0)
    {
        if (window.UIUtils)
        {
            window.UIUtils.showNotification('Ihr Warenkorb ist leer', 'warning');
        }
        return;
    }

    // Close cart modal
    const cartModal = document.getElementById('cartModal');
    if (cartModal)
    {
        if (window.UIUtils && window.UIUtils.safeCloseModal)
        {
            window.UIUtils.safeCloseModal(cartModal);
        }
        else
        {
            cartModal.remove();
            // Unlock scroll if safeCloseModal is not available
            if (window.UIUtils && window.UIUtils.unlockPageScroll)
            {
                window.UIUtils.unlockPageScroll();
            }
        }
    }

    // Show checkout page (matching old index.html style)
    showCheckoutPage();
}

/**
* Show checkout page (restored from old index.html)
*/
function showCheckoutPage()
{
    // Create checkout page
    const checkoutPage = document.createElement('div');
    checkoutPage.id = 'checkoutPage';
    // Check if mobile device
    const isMobile = window.innerWidth <= 768;

    checkoutPage.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${isMobile ? '#000000' : 'rgba(0,0,0,0.8)'}; /* Fully opaque on mobile to hide content below */
    z-index: ${isMobile ? '10001' : '11000'}; /* Above header (10000) on desktop */
    display: flex;
    align-items: center; /* Centered on mobile */
    justify-content: center;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding: ${isMobile ? '1rem' : '2rem'};
    `;

    // Global close button will be managed by UIUtils.updateGlobalCloseButton()

    const checkoutContent = document.createElement('div');
    checkoutContent.style.cssText = `
    background: white;
    border-radius: 15px;
    padding: ${isMobile ? '1rem' : '2rem'};
    max-width: ${isMobile ? 'calc(100vw - 2rem)' : '800px'};
    width: 100%;
    max-height: ${isMobile ? '90vh' : '90vh'}; /* Use full viewport height on mobile */
    overflow-y: auto;
    box-shadow: 0 20px 40px rgba(0,0,0,0.3);
    margin-top: 0;
    `;

    let checkoutHTML = `
    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 2rem;">
    <h2 style="color: #667eea; margin: 0;">Kasse</h2>
    </div>

    <!-- Order Summary -->
    <div style="max-width: 600px; margin: 0 auto;">
    <h3 style="color: #333; margin-bottom: 1rem;">Bestellübersicht</h3>
    <div style="background: #f8f9fa; padding: 1.5rem; border-radius: 10px; margin-bottom: 1rem;">
    `;

    // Add cart items to summary
    cart.forEach(item =>
    {
        checkoutHTML += `
        <div style="display: flex; justify-content: space-between; align-items: center; padding: 0.5rem 0; border-bottom: 1px solid #eee;">
        <div>
        <span style="font-weight: 600;">${item.name}</span>
        <span style="color: #666; margin-left: 0.5rem;">x${item.quantity}</span>
        </div>
        <span style="font-weight: 600; color: #667eea;">€${(item.price * item.quantity).toFixed(2)}</span>
        </div>
        `;
    });

    checkoutHTML += `
    </div>

    <div style="border-top: 2px solid #667eea; padding-top: 1rem;">
    <div style="display: flex; justify-content: space-between; margin-bottom: 0.5rem;">
    <span>Zwischensumme:</span>
    <span>€${cartTotal.toFixed(2)}</span>
    </div>
    <div style="display: flex; justify-content: space-between; margin-bottom: 0.5rem;">
    <span>Versand:</span>
    <span>Kostenlos</span>
    </div>
    <div style="display: flex; justify-content: space-between; margin-bottom: 1rem; font-size: 1.2rem; font-weight: bold; color: #667eea;">
    <span>Gesamt:</span>
    <span>€${cartTotal.toFixed(2)}</span>
    </div>
    </div>

    <!-- PayPal Pay Later Banner -->
    <div id="paypal-paylater-banner-checkout" 
         data-pp-message
         data-pp-style-layout="text"
         data-pp-style-logo-type="inline"
         data-pp-style-text-color="black"
         data-pp-amount="${cartTotal.toFixed(2)}"
         data-pp-language=""
         style="margin: 1rem 0; display: ${cartTotal > 0 ? 'block' : 'none'};"></div>

    <!-- Digital Services Notice -->
    <div style="background: #fff3cd; border: 1px solid #ffeaa7; border-radius: 8px; padding: 1rem; margin: 1.5rem 0; color: #856404;">
    <div style="display: flex; align-items: flex-start; gap: 0.5rem;">
    <span style="font-size: 1.1rem;">⚠️</span>
    <div>
    <strong style="display: block; margin-bottom: 0.5rem;">Hinweis zu digitalen Dienstleistungen:</strong>
    <p style="margin: 0; line-height: 1.4; font-size: 0.9rem;">
    Für digitale Dienstleistungen wie D-Luft Tuning-Software oder @Home MazdaEdit Lizenzen besteht <strong>kein Widerrufrecht</strong> gemäß § 356 Abs. 5 BGB. Mit der Bestellung stimmen Sie dem zu.
    </p>
    </div>
    </div>
    </div>

    <!-- Terms Agreement Checkboxes -->
    <div style="margin-top: 2rem; margin-bottom: 1.5rem; padding: 1.5rem; background: #f8f9fa; border-radius: 10px; border-left: 4px solid #667eea;">
    <h4 style="color: #333; margin: 0 0 1rem 0; font-size: 1.1rem;">Zustimmung erforderlich</h4>
    
    <div style="margin-bottom: 1rem;">
    <label style="display: flex; align-items: flex-start; cursor: pointer; font-size: 0.9rem; line-height: 1.4;">
    <input type="checkbox" id="agbCheckbox" style="margin-right: 0.75rem; margin-top: 0.1rem; width: 16px; height: 16px; cursor: pointer;">
    <span>Ich stimme den <a href="#" onclick="event.preventDefault(); window.showAGB && window.showAGB();" style="color: #667eea; text-decoration: underline; cursor: pointer;">Allgemeinen Geschäftsbedingungen</a> zu. <span style="color: #dc3545;">*</span></span>
    </label>
    </div>
    
    <div style="margin-bottom: 0.5rem;">
    <label style="display: flex; align-items: flex-start; cursor: pointer; font-size: 0.9rem; line-height: 1.4;">
    <input type="checkbox" id="rechtlicheHinweiseCheckbox" style="margin-right: 0.75rem; margin-top: 0.1rem; width: 16px; height: 16px; cursor: pointer;">
    <span>Ich habe die <a href="#" onclick="event.preventDefault(); window.showRechtlicheHinweise && window.showRechtlicheHinweise();" style="color: #667eea; text-decoration: underline; cursor: pointer;">Rechtliche Hinweise</a> zur Kenntnis genommen. <span style="color: #dc3545;">*</span></span>
    </label>
    </div>
    
    <p style="margin: 1rem 0 0 0; color: #666; font-size: 0.8rem; font-style: italic;">
    <span style="color: #dc3545;">*</span> Erforderlich für die Bestellung
    </p>
    </div>

    <!-- Payment Method Selection -->
    <div style="margin-top: 2rem;">
    <h3 style="color: #333; margin-bottom: 1rem;">Zahlungsmethode</h3>

    <!-- Bank Transfer Button -->
    <button id="bankTransferButton" onclick="showBankTransferDialog()" disabled style="width: 100%; background: #ccc; color: #666; border: none; padding: 15px 24px; border-radius: 8px; cursor: not-allowed; font-weight: 600; font-size: 1rem; margin-bottom: 1rem; display: flex; align-items: center; justify-content: center; gap: 0.5rem; box-sizing: border-box; min-height: 48px; max-width: 100%;">
    <span>🏦</span>
    <span>Rechnung</span>
    </button>

    <!-- Divider -->
    <div style="display: flex; align-items: center; margin: 1.5rem 0;">
    <div style="flex: 1; height: 1px; background: #ddd;"></div>
    <span style="padding: 0 1rem; color: #666; font-size: 0.9rem;">oder</span>
    <div style="flex: 1; height: 1px; background: #ddd;"></div>
    </div>
    </div>

    <!-- PayPal Payment Buttons -->
    <div id="paypal-button-container" style="margin-top: 1rem; opacity: 0.5; pointer-events: none;"></div>

    <p style="text-align: center; color: #666; font-size: 0.9rem; margin-top: 1rem;">
    🔒 Sichere Kasse powered by PayPal
    </p>
    </div>
    `;

    checkoutContent.innerHTML = checkoutHTML;
    checkoutPage.appendChild(checkoutContent);
    document.body.appendChild(checkoutPage);

    // Add checkbox event listeners for payment button enabling/disabling
    const agbCheckbox = document.getElementById('agbCheckbox');
    const rechtlicheHinweiseCheckbox = document.getElementById('rechtlicheHinweiseCheckbox');
    const bankTransferButton = document.getElementById('bankTransferButton');
    const paypalContainer = document.getElementById('paypal-button-container');

    function updatePaymentButtonsState()
    {
        const agbChecked = agbCheckbox && agbCheckbox.checked;
        const rechtlicheHinweiseChecked = rechtlicheHinweiseCheckbox && rechtlicheHinweiseCheckbox.checked;
        const bothChecked = agbChecked && rechtlicheHinweiseChecked;

        // Update bank transfer button
        if (bankTransferButton)
        {
            if (bothChecked)
            {
                bankTransferButton.disabled = false;
                bankTransferButton.style.background = '#667eea';
                bankTransferButton.style.color = 'white';
                bankTransferButton.style.cursor = 'pointer';
            } else
            {
                bankTransferButton.disabled = true;
                bankTransferButton.style.background = '#ccc';
                bankTransferButton.style.color = '#666';
                bankTransferButton.style.cursor = 'not-allowed';
            }
        }

        // Update PayPal container
        if (paypalContainer)
        {
            if (bothChecked)
            {
                paypalContainer.style.opacity = '1';
                paypalContainer.style.pointerEvents = 'auto';
            } else
            {
                paypalContainer.style.opacity = '0.5';
                paypalContainer.style.pointerEvents = 'none';
            }
        }
    }

    // Add event listeners to checkboxes
    if (agbCheckbox)
    {
        agbCheckbox.addEventListener('change', updatePaymentButtonsState);
    }
    if (rechtlicheHinweiseCheckbox)
    {
        rechtlicheHinweiseCheckbox.addEventListener('change', updatePaymentButtonsState);
    }

    // Initial state update
    updatePaymentButtonsState();

    // Update PayPal Pay Later banner with current cart total
    updatePayPalPayLaterBanner('paypal-paylater-banner-checkout', cartTotal);

    // Lock page scroll when checkout page is opened
    if (window.UIUtils && window.UIUtils.lockPageScroll)
    {
        window.UIUtils.lockPageScroll();
    }

    // Update global close button
    if (window.UIUtils && window.UIUtils.updateGlobalCloseButton)
    {
        window.UIUtils.updateGlobalCloseButton();
    }

    // Show loading indicator in PayPal container
    const container = document.getElementById('paypal-button-container');
    if (container)
    {
        container.innerHTML = '<p style="text-align: center; padding: 1rem; color: #666;">PayPal wird geladen...</p>';
    }

    // Check if PayPal was already preloaded when cart was opened
    // Note: isMobile is already declared at the start of this function

    // Check if PayPal is already loaded
    const isPayPalAlreadyLoaded = typeof paypal !== 'undefined' && window.PayPalIntegration;

    console.log('Loading PayPal on checkout:', {
        isMobile,
        userAgent: navigator.userAgent,
        wasPreloaded: paypalPreloadAttempted,
        hasPreloadPromise: !!paypalPreloadPromise,
        isAlreadyLoaded: isPayPalAlreadyLoaded
    });

    // If PayPal is already loaded, skip loading
    // If PayPal was preloaded, wait for the preload promise
    // Otherwise, load it now
    let paypalLoadPromise;
    if (isPayPalAlreadyLoaded)
    {
        // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('PayPal already loaded, skipping load');
        paypalLoadPromise = Promise.resolve();
    }
    else if (paypalPreloadPromise)
    {
        // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('Waiting for PayPal preload to complete');
        paypalLoadPromise = paypalPreloadPromise;
    }
    else
    {
        // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('Loading PayPal now (was not preloaded)');
        paypalLoadPromise = loadPayPalIntegrationModule()
            .then(() =>
            {
                // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('PayPal integration module loaded successfully');
                return loadPayPalSDK();
            });
    }

    paypalLoadPromise
        .then(() =>
        {
            // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('PayPal SDK ready');
            // Add a small delay on mobile to ensure everything is ready
            return new Promise((resolve) =>
            {
                setTimeout(() =>
                {
                    resolve();
                }, isMobile ? 500 : 200);
            });
        })
        .then(() =>
        {
            // Initialize PayPal buttons after SDK and module are loaded
            if (window.PayPalIntegration)
            {
                window.PayPalIntegration.initializePayPalButtons();
            }
            else
            {
                throw new Error('PayPalIntegration module not available after loading');
            }
        })
        .catch((error) =>
        {
            console.error('Failed to load PayPal:', error);
            const container = document.getElementById('paypal-button-container');
            if (container)
            {
                container.innerHTML = `
                    <div style="text-align: center; padding: 1rem; color: #dc3545;">
                        <p>PayPal konnte nicht geladen werden.</p>
                        <p style="font-size: 0.9rem; color: #666; margin-top: 0.5rem;">${isMobile ? 'Bitte überprüfen Sie Ihre Internetverbindung.' : 'Bitte versuchen Sie es später erneut.'}</p>
                        <button onclick="window.ShoppingCart.retryPayPalLoading()" style="background: #007bff; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer; margin-top: 0.5rem;">Erneut versuchen</button>
                    </div>
                `;
            }
            if (window.UIUtils)
            {
                window.UIUtils.showNotification('PayPal konnte nicht geladen werden. Bitte versuchen Sie es später erneut.', 'error');
            }
        });

    // Remove click-outside-to-close functionality - users must use the X button
}

/**
* Close checkout
*/
function closeCheckout()
{
    const checkoutPage = document.getElementById('checkoutPage');
    if (checkoutPage)
    {
        checkoutPage.remove();
        // Update global close button
        if (window.UIUtils && window.UIUtils.updateGlobalCloseButton)
        {
            window.UIUtils.updateGlobalCloseButton();
        }
        // Unlock scroll when checkout page is closed
        if (window.UIUtils && window.UIUtils.unlockPageScroll)
        {
            window.UIUtils.unlockPageScroll();
        }
    }
}

/**
* Simulate PayPal payment and process order
*/
async function simulatePayPalPayment()
{
    const emailInput = document.getElementById('orderEmail');
    const email = emailInput ? emailInput.value.trim() : '';

    if (!email)
    {
        if (window.UIUtils)
        {
            window.UIUtils.showNotification('Bitte geben Sie eine E-Mail-Adresse ein.', 'warning');
        }
        return;
    }

    if (!email.includes('@'))
    {
        if (window.UIUtils)
        {
            window.UIUtils.showNotification('Bitte geben Sie eine gültige E-Mail-Adresse ein.', 'warning');
        }
        return;
    }

    if (cart.length === 0)
    {
        if (window.UIUtils)
        {
            window.UIUtils.showNotification('Ihr Warenkorb ist leer.', 'warning');
        }
        return;
    }

    try
    {
        // Show loading state
        if (window.UIUtils)
        {
            window.UIUtils.showNotification('Zahlung wird verarbeitet...', 'info');
        }

        // Prepare order data
        const orderData = {
            email: email,
            items: cart.map(item => ({
                name: item.name,
                price: item.price,
                quantity: item.quantity,
                description: item.description
            })),
            total: cartTotal,
            paymentMethod: 'PayPal',
            status: 'paid'
        };

        // Send order to server
        const response = await fetch('/api/simulate-payment', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(orderData)
        });

        const result = await response.json();

        if (response.ok)
        {
            // Clear cart and close modal
            cart = [];
            updateCartDisplay();
            saveCartToStorage();
            toggleCart();

            if (window.UIUtils)
            {
                window.UIUtils.showNotification('Zahlung erfolgreich! Bestätigung wurde per E-Mail gesendet.', 'success');
            }
        }
        else
        {
            if (window.UIUtils)
            {
                window.UIUtils.showNotification(result.error || 'Zahlung fehlgeschlagen', 'error');
            }
        }
    }
    catch (error)
    {
        console.error('Payment simulation error:', error);
        if (window.UIUtils)
        {
            window.UIUtils.showNotification('Verbindungsfehler bei der Zahlung', 'error');
        }
    }
}

// Export functions for use in other modules
/**
* Show bank transfer dialog for address entry
*/
async function showBankTransferDialog()
{
    // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('🏦 Showing bank transfer dialog');

    // Check if required checkboxes are checked
    const agbCheckbox = document.getElementById('agbCheckbox');
    const rechtlicheHinweiseCheckbox = document.getElementById('rechtlicheHinweiseCheckbox');

    if (!agbCheckbox || !agbCheckbox.checked)
    {
        if (window.UIUtils)
        {
            window.UIUtils.showNotification('Bitte stimmen Sie den Allgemeinen Geschäftsbedingungen zu.', 'warning');
        }
        return;
    }

    if (!rechtlicheHinweiseCheckbox || !rechtlicheHinweiseCheckbox.checked)
    {
        if (window.UIUtils)
        {
            window.UIUtils.showNotification('Bitte nehmen Sie die Rechtliche Hinweise zur Kenntnis.', 'warning');
        }
        return;
    }

    // Close checkout page if it's still open
    const checkoutPage = document.getElementById('checkoutPage');
    if (checkoutPage)
    {
        checkoutPage.remove();
        // Unlock page scroll if it was locked
        if (window.UIUtils && window.UIUtils.unlockPageScroll)
        {
            window.UIUtils.unlockPageScroll();
        }
    }

    // Remove any existing bank transfer modal first
    const existingModal = document.getElementById('bankTransferModal');
    if (existingModal)
    {
        existingModal.remove();
    }

    // Get current user data if logged in
    const currentUser = window.AccountManagement ? window.AccountManagement.getCurrentUser() : null;
    const authToken = window.AccountManagement ? window.AccountManagement.getAuthToken() : null;

    // Fetch fresh profile data from API if user is logged in
    let userProfile = null;
    if (currentUser && authToken)
    {
        try
        {
            const response = await fetch('/api/profile', {
                headers: { 'Authorization': `Bearer ${authToken}` }
            });

            if (response.ok)
            {
                const profileData = await response.json();
                userProfile = profileData.user;
            }
            else
            {
                // Fallback to cached user data if API call fails
                userProfile = currentUser;
            }
        }
        catch (error)
        {
            console.error('Error fetching user profile:', error);
            // Fallback to cached user data if API call fails
            userProfile = currentUser;
        }
    }

    // Create modal
    const modal = document.createElement('div');
    modal.id = 'bankTransferModal';
    const isMobile = window.innerWidth <= 768;

    modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${isMobile ? '#000000' : 'rgba(0,0,0,0.8)'}; /* Fully opaque on mobile to hide content below */
    z-index: ${isMobile ? '10002' : '11000'}; /* Above checkout page (10001/4000) and header (10000) on desktop */
    display: flex;
    align-items: center; /* Centered on mobile */
    justify-content: center;
    padding: 20px;
    box-sizing: border-box;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    `;

    // Global close button will be managed by UIUtils.updateGlobalCloseButton()

    const modalContent = document.createElement('div');
    modalContent.style.cssText = `
    background: white;
    border-radius: 12px;
    padding: 2rem;
    max-width: 600px;
    width: 100%;
    max-height: ${isMobile ? '90vh' : '90vh'}; /* Use full viewport height on mobile */
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    `;

    // Pre-fill addresses if user is logged in - use fresh profile data if available
    const defaultInvoiceAddress = userProfile ? (userProfile.address || '') : '';
    const defaultInvoiceCity = userProfile ? (userProfile.city || '') : '';
    const defaultInvoiceZip = userProfile ? (userProfile.postal_code || '') : '';
    const defaultCountry = userProfile ? (userProfile.country || 'Germany') : 'Germany';
    const defaultEmail = userProfile ? (userProfile.email || '') : '';
    const defaultName = userProfile ? (userProfile.name || '') : '';

    modalContent.innerHTML = `
    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem;">
    <h2 style="color: #667eea; margin: 0;">🏦 Bestellung auf Rechnung</h2>
    </div>

    <form id="bankTransferForm">
    <div style="margin-bottom: 1.5rem;">
    <h3 style="color: #333; margin-bottom: 1rem; font-size: 1.1rem;">📧 Ihre Kontaktdaten</h3>
    <div style="margin-bottom: 1rem;">
    <label style="display: block; margin-bottom: 0.5rem; color: #333; font-weight: 600;">E-Mail-Adresse:*</label>
    <input type="email" id="bankTransferEmail" value="${defaultEmail}" required style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px;">
    </div>
    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-bottom: 1rem;">
    <div>
    <label style="display: block; margin-bottom: 0.5rem; color: #333; font-weight: 600;">Vorname:*</label>
    <input type="text" id="bankTransferFirstName" value="${defaultName ? defaultName.split(' ')[0] : ''}" required style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px;">
    </div>
    <div>
    <label style="display: block; margin-bottom: 0.5rem; color: #333; font-weight: 600;">Nachname:*</label>
    <input type="text" id="bankTransferLastName" value="${defaultName ? defaultName.split(' ').slice(1).join(' ') : ''}" required style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px;">
    </div>
    </div>
    </div>

    <div style="margin-bottom: 1.5rem;">
    <h3 style="color: #333; margin-bottom: 1rem; font-size: 1.1rem;">📍 Rechnungsadresse</h3>
    <div style="margin-bottom: 1rem;">
    <label style="display: block; margin-bottom: 0.5rem; color: #333; font-weight: 600;">Straße:*</label>
    <input type="text" id="bankTransferInvoiceAddress" value="${defaultInvoiceAddress}" required style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px;">
    </div>
    <div style="display: grid; grid-template-columns: 0.7fr 2.3fr; gap: 1rem; margin-bottom: 1rem;">
    <div>
    <label style="display: block; margin-bottom: 0.5rem; color: #333; font-weight: 600;">Postleitzahl:*</label>
    <input type="text" id="bankTransferInvoiceZip" value="${defaultInvoiceZip}" required pattern="[0-9]*" inputmode="numeric" style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px;">
    </div>
    <div>
    <label style="display: block; margin-bottom: 0.5rem; color: #333; font-weight: 600;">Stadt:*</label>
    <input type="text" id="bankTransferInvoiceCity" value="${defaultInvoiceCity}" required style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px;">
    </div>
    </div>
    <div style="margin-bottom: 1rem;">
    <label style="display: block; margin-bottom: 0.5rem; color: #333; font-weight: 600;">Land:*</label>
    <input type="text" id="bankTransferInvoiceCountry" value="${defaultCountry}" required style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px;">
    </div>
    </div>

    <div style="margin-bottom: 1.5rem;">
    <h3 style="color: #333; margin-bottom: 1rem; font-size: 1.1rem;">🚚 Lieferadresse</h3>
    <div style="margin-bottom: 1rem;">
    <label style="display: flex; align-items: center; cursor: pointer;">
    <input type="checkbox" id="bankTransferSameAddress" checked style="margin-right: 0.5rem; width: 18px; height: 18px;">
    <span style="color: #333; font-weight: 600;">Gleich wie Rechnungsadresse</span>
    </label>
    </div>
    <div id="bankTransferShippingAddressFields" style="display: none;">
    <div style="margin-bottom: 1rem;">
    <label style="display: block; margin-bottom: 0.5rem; color: #333; font-weight: 600;">Straße:*</label>
    <input type="text" id="bankTransferShippingAddress" name="bankTransferShippingAddress" value="${defaultInvoiceAddress}" style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px;">
    </div>
    <div style="display: grid; grid-template-columns: 0.7fr 2.3fr; gap: 1rem; margin-bottom: 1rem;">
    <div>
    <label style="display: block; margin-bottom: 0.5rem; color: #333; font-weight: 600;">Postleitzahl:*</label>
    <input type="text" id="bankTransferShippingZip" name="bankTransferShippingZip" value="${defaultInvoiceZip}" pattern="[0-9]*" inputmode="numeric" style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px;">
    </div>
    <div>
    <label style="display: block; margin-bottom: 0.5rem; color: #333; font-weight: 600;">Stadt:*</label>
    <input type="text" id="bankTransferShippingCity" name="bankTransferShippingCity" value="${defaultInvoiceCity}" style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px;">
    </div>
    </div>
    <div style="margin-bottom: 1rem;">
    <label style="display: block; margin-bottom: 0.5rem; color: #333; font-weight: 600;">Land:*</label>
    <input type="text" id="bankTransferShippingCountry" name="bankTransferShippingCountry" value="${defaultCountry}" style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px;">
    </div>
    </div>
    </div>

    <div style="display: flex; gap: 1rem; margin-top: 2rem;">
    <button type="button" onclick="closeBankTransferDialog()" id="bankTransferCancelBtn" style="flex: 1; background: #6c757d; color: white; border: none; padding: 12px 24px; border-radius: 8px; cursor: pointer; font-weight: 600;">Abbrechen</button>
    <button type="submit" id="bankTransferSubmitBtn" style="flex: 1; background: #28a745; color: white; border: none; padding: 12px 24px; border-radius: 8px; cursor: pointer; font-weight: 600;">
    <span id="bankTransferSubmitText">Bestellen</span>
    <span id="bankTransferSpinner" style="display: none;">
    <svg style="width: 20px; height: 20px; animation: spin 1s linear infinite;" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none" opacity="0.25"></circle>
    <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" opacity="0.75"></path>
    </svg>
    </span>
    </button>
    </div>
    </form>
    <style>
    @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
    }
    </style>
    `;

    modal.appendChild(modalContent);
    document.body.appendChild(modal);

    // Lock page scroll when bank transfer modal is opened
    if (window.UIUtils && window.UIUtils.lockPageScroll)
    {
        window.UIUtils.lockPageScroll();
    }

    // Update global close button
    if (window.UIUtils && window.UIUtils.updateGlobalCloseButton)
    {
        window.UIUtils.updateGlobalCloseButton();
    }

    // Handle checkbox to show/hide shipping address fields
    const checkbox = document.getElementById('bankTransferSameAddress');
    const shippingFields = document.getElementById('bankTransferShippingAddressFields');

    // Initialize: Set fields to not required since checkbox is checked by default
    if (checkbox.checked)
    {
        document.getElementById('bankTransferShippingAddress').required = false;
        document.getElementById('bankTransferShippingZip').required = false;
        document.getElementById('bankTransferShippingCity').required = false;
        document.getElementById('bankTransferShippingCountry').required = false;
    }

    checkbox.addEventListener('change', function ()
    {
        if (this.checked)
        {
            shippingFields.style.display = 'none';
            // Make shipping address fields not required when hidden
            document.getElementById('bankTransferShippingAddress').required = false;
            document.getElementById('bankTransferShippingZip').required = false;
            document.getElementById('bankTransferShippingCity').required = false;
            document.getElementById('bankTransferShippingCountry').required = false;
        }
        else
        {
            shippingFields.style.display = 'block';
            // Make shipping address fields required when visible
            document.getElementById('bankTransferShippingAddress').required = true;
            document.getElementById('bankTransferShippingZip').required = true;
            document.getElementById('bankTransferShippingCity').required = true;
            document.getElementById('bankTransferShippingCountry').required = true;
        }
    });

    // Restrict postal code inputs to numbers only
    const postalCodeInputs = [
        document.getElementById('bankTransferInvoiceZip'),
        document.getElementById('bankTransferShippingZip')
    ];

    postalCodeInputs.forEach(input =>
    {
        if (input)
        {
            input.addEventListener('input', function (e)
            {
                // Remove any non-numeric characters
                this.value = this.value.replace(/[^0-9]/g, '');
            });

            input.addEventListener('keypress', function (e)
            {
                // Allow: backspace, delete, tab, escape, enter
                if ([8, 9, 27, 13, 46].indexOf(e.keyCode) !== -1 ||
                    // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                    (e.keyCode === 65 && e.ctrlKey === true) ||
                    (e.keyCode === 67 && e.ctrlKey === true) ||
                    (e.keyCode === 86 && e.ctrlKey === true) ||
                    (e.keyCode === 88 && e.ctrlKey === true))
                {
                    return;
                }
                // Ensure that it is a number and stop the keypress
                if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105))
                {
                    e.preventDefault();
                }
            });
        }
    });

    // Handle form submission
    document.getElementById('bankTransferForm').addEventListener('submit', async (e) =>
    {
        e.preventDefault();
        await showBankTransferConfirmationDialog();
    });

    // Remove click-outside-to-close functionality - users must use the X button
}

/**
* Close bank transfer dialog
*/
function closeBankTransferDialog()
{
    const modal = document.getElementById('bankTransferModal');
    if (modal)
    {
        // Remove the modal
        modal.remove();

        // Also ensure checkout page is closed if it exists
        const checkoutPage = document.getElementById('checkoutPage');
        if (checkoutPage)
        {
            checkoutPage.remove();
        }

        // Unlock page scroll
        if (window.UIUtils && window.UIUtils.unlockPageScroll)
        {
            window.UIUtils.unlockPageScroll();
        }

        // Update global close button after a small delay to ensure DOM updates
        if (window.UIUtils && window.UIUtils.updateGlobalCloseButton)
        {
            setTimeout(() =>
            {
                window.UIUtils.updateGlobalCloseButton();
            }, 50);
        }
    }
}

/**
* Show confirmation dialog before processing bank transfer order
*/
async function showBankTransferConfirmationDialog()
{
    // Get cart data
    const cartData = window.ShoppingCart ? window.ShoppingCart.getCartData() : { items: [], total: 0 };

    if (!cartData.items || cartData.items.length === 0)
    {
        if (window.UIUtils)
        {
            window.UIUtils.showNotification('Warenkorb ist leer', 'error');
        }
        return;
    }

    // Create confirmation modal
    const modal = document.createElement('div');
    modal.id = 'bankTransferConfirmationModal';
    const isMobile = window.innerWidth <= 768;

    modal.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: ${isMobile ? '#000000' : 'rgba(0,0,0,0.8)'};
        z-index: ${isMobile ? '10003' : '11001'}; /* Above bank transfer modal (10002/11000) */
        display: flex;
        align-items: center;
        justify-content: center;
    `;
    const modalContent = document.createElement('div');
    modalContent.style.cssText = `
        background: white;
        border-radius: 15px;
        padding: ${isMobile ? '1.5rem' : '2rem'};
        max-width: ${isMobile ? 'calc(100vw - 2rem)' : '600px'};
        width: 95%;
        max-height: 90vh;
        overflow-y: auto;
        box-shadow: 0 20px 40px rgba(0,0,0,0.3);
        position: relative;
    `;

    // Build items list HTML
    let itemsHTML = '';
    cartData.items.forEach((item) =>
    {
        const itemPrice = (item.price !== undefined && item.price !== null) ? Number(item.price) : 0;
        const quantity = item.quantity || 1;
        itemsHTML += `
            <div style="padding: 0.75rem; border: 1px solid #eee; border-radius: 8px; margin-bottom: 0.5rem; background: #f8f9fa;">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                    <div style="flex: 1;">
                        <strong style="color: #333;">${item.name || 'Unbenannter Artikel'}</strong>
                        ${item.description ? `<p style="margin: 0.25rem 0 0 0; color: #666; font-size: 0.9rem;">${item.description}</p>` : ''}
                    </div>
                    <div style="text-align: right; margin-left: 1rem;">
                        <div style="font-weight: 600; color: #667eea;">€${itemPrice.toFixed(2)}</div>
                        ${quantity > 1 ? `<div style="font-size: 0.85rem; color: #666;">× ${quantity}</div>` : ''}
                    </div>
                </div>
            </div>
        `;
    });

    modalContent.innerHTML = `
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem;">
            <h2 style="color: #667eea; margin: 0;">Bestellung bestätigen</h2>
            <button onclick="closeBankTransferConfirmationDialog()" style="background: none; border: none; font-size: 1.5rem; cursor: pointer; color: #666; padding: 0; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center;">&times;</button>
        </div>
        
        <div style="margin-bottom: 1.5rem;">
            <p style="color: #333; font-size: 1.1rem; margin-bottom: 1rem; font-weight: 600;">Sind Sie sicher, dass Sie die folgenden Artikel bestellen möchten?</p>
            
            <div style="max-height: 300px; overflow-y: auto; margin-bottom: 1rem;">
                ${itemsHTML}
            </div>
            
            <div style="border-top: 2px solid #667eea; padding-top: 1rem; margin-top: 1rem;">
                <div style="display: flex; justify-content: space-between; align-items: center; font-size: 1.2rem; font-weight: 600; color: #333;">
                    <span>Gesamt:</span>
                    <span style="color: #667eea;">€${cartData.total.toFixed(2)}</span>
                </div>
            </div>
        </div>

        <div style="display: flex; gap: 1rem; margin-top: 2rem;">
            <button type="button" onclick="closeBankTransferConfirmationDialog()" style="flex: 1; background: #6c757d; color: white; border: none; padding: 12px 24px; border-radius: 8px; cursor: pointer; font-weight: 600;">Abbrechen</button>
            <button type="button" onclick="confirmBankTransferOrder()" style="flex: 1; background: #28a745; color: white; border: none; padding: 12px 24px; border-radius: 8px; cursor: pointer; font-weight: 600;">Jetzt kaufen</button>
        </div>
    `;

    modal.appendChild(modalContent);
    document.body.appendChild(modal);

    // Lock page scroll when confirmation modal is opened
    if (window.UIUtils && window.UIUtils.lockPageScroll)
    {
        window.UIUtils.lockPageScroll();
    }

    // Update global close button
    if (window.UIUtils && window.UIUtils.updateGlobalCloseButton)
    {
        requestAnimationFrame(() =>
        {
            window.UIUtils.updateGlobalCloseButton();
        });
    }

    // Handle modal close with proper scroll unlock
    const closeModal = () =>
    {
        if (window.UIUtils && window.UIUtils.safeCloseModal)
        {
            window.UIUtils.safeCloseModal(modal);
        }
        else
        {
            modal.remove();
            if (window.UIUtils && window.UIUtils.unlockPageScroll)
            {
                window.UIUtils.unlockPageScroll();
            }
        }
        // Update global close button after closing
        if (window.UIUtils && window.UIUtils.updateGlobalCloseButton)
        {
            window.UIUtils.updateGlobalCloseButton();
        }
    };

    // Prevent closing modal on backdrop click - only X button can close
    const isMobileModal = window.innerWidth <= 768;
    let mouseDownTargetModal = null;

    modal.addEventListener('mousedown', (e) =>
    {
        mouseDownTargetModal = e.target;
    });

    modal.addEventListener('click', (e) =>
    {
        // Only allow backdrop click to close on mobile, not desktop
        if (e.target === modal && isMobileModal && mouseDownTargetModal === modal)
        {
            closeModal();
        }
        mouseDownTargetModal = null;
    });
}

/**
* Close bank transfer confirmation dialog
*/
function closeBankTransferConfirmationDialog()
{
    const modal = document.getElementById('bankTransferConfirmationModal');
    if (modal)
    {
        modal.remove();

        // Unlock page scroll
        if (window.UIUtils && window.UIUtils.unlockPageScroll)
        {
            window.UIUtils.unlockPageScroll();
        }

        // Update global close button
        if (window.UIUtils && window.UIUtils.updateGlobalCloseButton)
        {
            setTimeout(() =>
            {
                window.UIUtils.updateGlobalCloseButton();
            }, 50);
        }
    }
}

/**
* Confirm and process bank transfer order
*/
async function confirmBankTransferOrder()
{
    // Close confirmation dialog
    closeBankTransferConfirmationDialog();

    // Process the order
    await processBankTransferOrder();
}

/**
* Process bank transfer order
*/
async function processBankTransferOrder()
{
    // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('🔄 Processing bank transfer order...');

    try
    {
        // Get form data
        const email = document.getElementById('bankTransferEmail').value.trim();
        const firstName = document.getElementById('bankTransferFirstName').value.trim();
        const lastName = document.getElementById('bankTransferLastName').value.trim();
        const name = `${firstName} ${lastName}`.trim();
        const invoiceAddress = document.getElementById('bankTransferInvoiceAddress').value.trim();
        const invoiceZip = document.getElementById('bankTransferInvoiceZip').value.trim();
        const invoiceCity = document.getElementById('bankTransferInvoiceCity').value.trim();
        const invoiceCountry = document.getElementById('bankTransferInvoiceCountry').value.trim();
        const sameAddress = document.getElementById('bankTransferSameAddress').checked;

        let shippingAddress = invoiceAddress;
        let shippingZip = invoiceZip;
        let shippingCity = invoiceCity;
        let shippingCountry = invoiceCountry;

        if (!sameAddress)
        {
            shippingAddress = document.getElementById('bankTransferShippingAddress').value.trim();
            shippingZip = document.getElementById('bankTransferShippingZip').value.trim();
            shippingCity = document.getElementById('bankTransferShippingCity').value.trim();
            shippingCountry = document.getElementById('bankTransferShippingCountry').value.trim();
        }

        // Validate all fields
        if (!email || !firstName || !lastName || !invoiceAddress || !invoiceZip || !invoiceCity || !invoiceCountry)
        {
            if (window.UIUtils)
            {
                window.UIUtils.showNotification('Bitte füllen Sie alle Pflichtfelder aus', 'error');
            }
            return;
        }

        if (!sameAddress && (!shippingAddress || !shippingZip || !shippingCity || !shippingCountry))
        {
            if (window.UIUtils)
            {
                window.UIUtils.showNotification('Bitte füllen Sie alle Lieferadressfelder aus', 'error');
            }
            return;
        }

        // Get cart data
        const cartData = window.ShoppingCart ? window.ShoppingCart.getCartData() : { items: [], total: 0 };

        if (!cartData.items || cartData.items.length === 0)
        {
            if (window.UIUtils)
            {
                window.UIUtils.showNotification('Warenkorb ist leer', 'error');
            }
            return;
        }

        // Show spinner and disable buttons
        const submitBtn = document.getElementById('bankTransferSubmitBtn');
        const cancelBtn = document.getElementById('bankTransferCancelBtn');
        const submitText = document.getElementById('bankTransferSubmitText');
        const spinner = document.getElementById('bankTransferSpinner');

        if (submitBtn && cancelBtn && submitText && spinner)
        {
            submitBtn.disabled = true;
            cancelBtn.disabled = true;
            submitBtn.style.opacity = '0.7';
            submitBtn.style.cursor = 'not-allowed';
            cancelBtn.style.opacity = '0.7';
            cancelBtn.style.cursor = 'not-allowed';
            submitText.style.display = 'none';
            spinner.style.display = 'inline-block';
        }

        // Disable ALL buttons in the checkout page to prevent any interaction during processing
        const checkoutPage = document.getElementById('checkoutPage');
        if (checkoutPage)
        {
            const allButtons = checkoutPage.querySelectorAll('button');
            allButtons.forEach(btn =>
            {
                btn.disabled = true;
                btn.style.opacity = '0.5';
                btn.style.cursor = 'not-allowed';
                btn.style.pointerEvents = 'none';
            });
        }

        // Also disable PayPal buttons if they exist
        const paypalContainer = document.getElementById('paypal-button-container');
        if (paypalContainer)
        {
            paypalContainer.style.pointerEvents = 'none';
            paypalContainer.style.opacity = '0.5';
        }

        // Get auth token if logged in
        const authToken = window.AccountManagement ? window.AccountManagement.getAuthToken() : null;

        // Prepare customer info
        const customerInfo = {
            name: name,
            email: email,
            address: invoiceAddress,
            city: invoiceCity,
            postal_code: invoiceZip,
            country: invoiceCountry
        };

        // Prepare order data
        const orderData = {
            total: cartData.total,
            items: cartData.items,
            status: 'NOT_PAID',
            customerInfo: customerInfo,
            shippingAddress: {
                address: shippingAddress,
                city: shippingCity,
                postal_code: shippingZip,
                country: shippingCountry
            }
        };

        // Send order to API
        // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('📤 Sending bank transfer order to API...');
        const response = await fetch('/api/orders/bank-transfer', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(authToken && { 'Authorization': `Bearer ${authToken}` })
            },
            body: JSON.stringify(orderData)
        });

        const data = await response.json();

        if (response.ok && data.success)
        {
            // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('✅ Bank transfer order created successfully:', data);

            // Store order ID for confirmation dialog
            window.lastSavedOrderId = data.orderId;
            
            // Capture cart data BEFORE clearing
            const cartData = window.ShoppingCart.getCartData();
            const orderItems = cartData.items || [];
            const orderTotal = cartData.total || 0;
            
            // Store items and customer info for confirmation dialog
            window.lastOrderItems = orderItems;
            window.lastOrderTotal = orderTotal;
            window.lastOrderCustomerInfo = customerInfo;

            // Close dialog
            closeBankTransferDialog();

            // Close checkout dialog
            closeCheckout();

            // Clear cart
            window.ShoppingCart.clearCart();

            // Show order confirmation dialog
            if (data.orderId && window.PayPalIntegration && window.PayPalIntegration.showOrderConfirmationDialog)
            {
                window.PayPalIntegration.showOrderConfirmationDialog(
                    data.orderId,
                    { transactionId: data.orderId }, // Payment details for bank transfer
                    orderTotal,
                    orderItems,
                    'Bank Transfer'
                );
            }
            else
            {
                // Fallback to notification
                if (window.UIUtils)
                {
                    window.UIUtils.showNotification('Bestellung erfolgreich erstellt! Sie erhalten eine Bestätigung per E-Mail.', 'success');
                }
            }
        }
        else
        {
            console.error('❌ Failed to create bank transfer order:', data);

            // Re-enable buttons and hide spinner on error
            const submitBtn = document.getElementById('bankTransferSubmitBtn');
            const cancelBtn = document.getElementById('bankTransferCancelBtn');
            const submitText = document.getElementById('bankTransferSubmitText');
            const spinner = document.getElementById('bankTransferSpinner');

            if (submitBtn && cancelBtn && submitText && spinner)
            {
                submitBtn.disabled = false;
                cancelBtn.disabled = false;
                submitBtn.style.opacity = '1';
                submitBtn.style.cursor = 'pointer';
                cancelBtn.style.opacity = '1';
                cancelBtn.style.cursor = 'pointer';
                submitText.style.display = 'inline';
                spinner.style.display = 'none';
            }

            // Re-enable ALL buttons in checkout page
            const checkoutPage = document.getElementById('checkoutPage');
            if (checkoutPage)
            {
                const allButtons = checkoutPage.querySelectorAll('button');
                allButtons.forEach(btn =>
                {
                    btn.disabled = false;
                    btn.style.opacity = '1';
                    btn.style.cursor = 'pointer';
                    btn.style.pointerEvents = 'auto';
                });
            }

            // Re-enable PayPal buttons
            const paypalContainer = document.getElementById('paypal-button-container');
            if (paypalContainer)
            {
                paypalContainer.style.pointerEvents = 'auto';
                paypalContainer.style.opacity = '1';
            }

            if (window.UIUtils)
            {
                window.UIUtils.showNotification(data.error || 'Fehler beim Erstellen der Bestellung', 'error');
            }
        }

    }
    catch (error)
    {
        console.error('❌ Error processing bank transfer order:', error);

        // Re-enable buttons and hide spinner on error
        const submitBtn = document.getElementById('bankTransferSubmitBtn');
        const cancelBtn = document.getElementById('bankTransferCancelBtn');
        const submitText = document.getElementById('bankTransferSubmitText');
        const spinner = document.getElementById('bankTransferSpinner');

        if (submitBtn && cancelBtn && submitText && spinner)
        {
            submitBtn.disabled = false;
            cancelBtn.disabled = false;
            submitBtn.style.opacity = '1';
            submitBtn.style.cursor = 'pointer';
            cancelBtn.style.opacity = '1';
            cancelBtn.style.cursor = 'pointer';
            submitText.style.display = 'inline';
            spinner.style.display = 'none';
        }

        // Re-enable ALL buttons in checkout page
        const checkoutPage = document.getElementById('checkoutPage');
        if (checkoutPage)
        {
            const allButtons = checkoutPage.querySelectorAll('button');
            allButtons.forEach(btn =>
            {
                btn.disabled = false;
                btn.style.opacity = '1';
                btn.style.cursor = 'pointer';
                btn.style.pointerEvents = 'auto';
            });
        }

        // Re-enable PayPal buttons
        const paypalContainer = document.getElementById('paypal-button-container');
        if (paypalContainer)
        {
            paypalContainer.style.pointerEvents = 'auto';
            paypalContainer.style.opacity = '1';
        }

        if (window.UIUtils)
        {
            window.UIUtils.showNotification('Fehler beim Verarbeiten der Bestellung', 'error');
        }
    }
}

/**
* Retry PayPal loading (called from error message button)
*/
function retryPayPalLoading()
{
    const container = document.getElementById('paypal-button-container');
    if (container)
    {
        container.innerHTML = '<p style="text-align: center; padding: 1rem; color: #666;">PayPal wird geladen...</p>';
    }

    const isMobile = window.innerWidth <= 768;

    loadPayPalIntegrationModule()
        .then(() =>
        {
            // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('PayPal integration module loaded successfully (retry)');
            return loadPayPalSDK();
        })
        .then(() =>
        {
            // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('PayPal SDK loaded successfully (retry)');
            // Add a small delay on mobile to ensure everything is ready
            return new Promise((resolve) =>
            {
                setTimeout(() =>
                {
                    resolve();
                }, isMobile ? 500 : 200);
            });
        })
        .then(() =>
        {
            if (window.PayPalIntegration)
            {
                window.PayPalIntegration.initializePayPalButtons();
            }
            else
            {
                throw new Error('PayPalIntegration module not available after retry');
            }
        })
        .catch((error) =>
        {
            console.error('Failed to retry PayPal loading:', error);
            if (container)
            {
                container.innerHTML = `
                    <div style="text-align: center; padding: 1rem; color: #dc3545;">
                        <p>PayPal konnte nicht geladen werden.</p>
                        <p style="font-size: 0.9rem; color: #666; margin-top: 0.5rem;">${isMobile ? 'Bitte überprüfen Sie Ihre Internetverbindung.' : 'Bitte versuchen Sie es später erneut.'}</p>
                        <button onclick="window.ShoppingCart.retryPayPalLoading()" style="background: #007bff; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer; margin-top: 0.5rem;">Erneut versuchen</button>
                    </div>
                `;
            }
            if (window.UIUtils)
            {
                window.UIUtils.showNotification('PayPal konnte nicht geladen werden. Bitte versuchen Sie es später erneut.', 'error');
            }
        });
}

window.ShoppingCart = {
    initializeShoppingCart,
    addToCart,
    removeFromCart,
    updateCartItemQuantity,
    clearCart,
    toggleCart,
    getCartData,
    extractCustomerInfo,
    proceedToCheckout,
    closeCheckout,
    simulatePayPalPayment,
    cleanupOrphanedOptions,
    loadPayPalSDK,
    loadPayPalIntegrationModule,
    retryPayPalLoading,
    updatePayPalPayLaterBanner
};

// Make functions globally available for onclick handlers
window.addToCart = addToCart;
window.toggleCart = toggleCart;
window.proceedToCheckout = proceedToCheckout;
window.closeCheckout = closeCheckout;
window.updatePayPalPayLaterBanner = updatePayPalPayLaterBanner;
window.removeFromCart = removeFromCart;
window.updateCartItemQuantity = updateCartItemQuantity;
window.clearCart = clearCart;
window.simulatePayPalPayment = simulatePayPalPayment;
window.showBankTransferDialog = showBankTransferDialog;
window.closeBankTransferDialog = closeBankTransferDialog;
window.closeBankTransferConfirmationDialog = closeBankTransferConfirmationDialog;
window.confirmBankTransferOrder = confirmBankTransferOrder;